import React, { useEffect, useState } from "react";
import { Sparkles, X, Lightbulb, Heart, RefreshCw } from "lucide-react";
import api from "../lib/api";

const MOODS = [
  { id: "great", label: "Great", emoji: "😄" },
  { id: "focused", label: "Focused", emoji: "🎯" },
  { id: "tired", label: "Tired", emoji: "😪" },
  { id: "stressed", label: "Stressed", emoji: "😣" },
  { id: "okay", label: "Okay", emoji: "🙂" },
];

const COMFORT = {
  great: "Beautiful — let's channel that into shipping something great today.",
  focused: "Lock in. I'll keep distractions minimal in the workspace.",
  tired: "It's okay to take it slow. Maybe knock out one small win and rest.",
  stressed: "Deep breath. We'll tackle one stage at a time. You've got this.",
  okay: "A steady day is still a productive day. Glad you're here.",
};

const MOOD_LABEL = {
  great: "great", focused: "focused", tired: "tired",
  stressed: "stressed", okay: "okay",
};

export default function AIGreeter() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const todayKey = `rachana-greeter-skip-${new Date().toDateString()}`;
    if (sessionStorage.getItem(todayKey) === "1") return;
    api.get("/dashboard/greeting").then(({ data }) => {
      setData(data);
      setOpen(true);
    }).catch(() => {/* fail silently */});
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: fresh } = await api.get("/dashboard/greeting");
      setData(fresh);
    } finally { setRefreshing(false); }
  };

  const pickMood = async (mood) => {
    setPicked(mood);
    try {
      await api.post("/dashboard/mood", { mood });
    } catch {/* never block UX on save */}
  };

  const close = (skipToday = false) => {
    if (skipToday) {
      const todayKey = `rachana-greeter-skip-${new Date().toDateString()}`;
      sessionStorage.setItem(todayKey, "1");
    }
    setOpen(false);
  };

  if (!open || !data) return null;

  return (
    <div data-testid="ai-greeter-overlay" style={{
      position: "fixed", inset: 0, background: "rgba(8,12,22,0.78)",
      backdropFilter: "blur(8px)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "greeterFade .35s ease",
    }}>
      <div data-testid="ai-greeter-modal" style={{
        maxWidth: 560, width: "100%", borderRadius: 18,
        background: "linear-gradient(155deg, #11192c 0%, #1c2845 65%, #2a3458 100%)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.75)",
        border: "1px solid rgba(242,107,31,0.25)",
        position: "relative", overflow: "hidden",
      }}>
        {/* Decorative orange glow */}
        <div style={{
          position: "absolute", top: -120, right: -120, width: 280, height: 280,
          background: "radial-gradient(circle, rgba(242,107,31,0.45), transparent 65%)",
          pointerEvents: "none",
        }} />

        <button data-testid="greeter-close-x" onClick={() => close(false)}
          style={{
            position: "absolute", top: 14, right: 14, background: "transparent",
            border: "none", color: "#9aa4bf", cursor: "pointer", padding: 6,
          }} aria-label="Close greeter">
          <X size={18} />
        </button>

        <div style={{ padding: "30px 30px 24px", position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Sparkles size={18} style={{ color: "#F26B1F" }} />
            <span style={{ fontSize: 11, letterSpacing: "0.18em",
                            color: "#FBBC04", fontWeight: 600 }}>
              CAPEXARA · COMPANION
            </span>
            <button data-testid="greeter-refresh" onClick={refresh}
              disabled={refreshing}
              style={{
                marginLeft: "auto", marginRight: 30, background: "transparent",
                border: "1px solid rgba(255,255,255,0.12)", color: "#9aa4bf",
                borderRadius: 14, padding: "3px 8px", fontSize: 10,
                cursor: refreshing ? "wait" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}>
              <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
              new vibe
            </button>
          </div>

          {data.last_mood && !data.last_mood.is_today && (
            <div data-testid="greeter-continuity" style={{
              fontSize: 11, color: "#cdd5e7", padding: "6px 10px",
              borderRadius: 6, marginBottom: 10,
              background: "rgba(122,184,255,0.10)",
              border: "1px solid rgba(122,184,255,0.20)",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <Heart size={11} style={{ color: "#7AB8FF" }} />
              Continuing from your last visit · you felt
              <strong style={{ color: "#7AB8FF" }}>
                &nbsp;{MOOD_LABEL[data.last_mood.mood] || data.last_mood.mood}
              </strong>
              {data.last_mood.hours_ago < 36
                ? ` ${Math.round(data.last_mood.hours_ago)}h ago`
                : ` ${Math.round(data.last_mood.hours_ago / 24)}d ago`}
            </div>
          )}

          <p data-testid="greeter-greeting" style={{
            fontSize: 18, lineHeight: 1.55, color: "#f7f9fd",
            margin: "0 0 18px", fontWeight: 400,
          }}>
            {data.greeting}
          </p>

          {/* Mood picker */}
          {!picked ? (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#9aa4bf",
                             letterSpacing: "0.06em", marginBottom: 8 }}>
                ONE TAP TO SHARE HOW YOU'RE FEELING
              </div>
              <div data-testid="greeter-moods" style={{
                display: "flex", gap: 8, flexWrap: "wrap",
              }}>
                {MOODS.map((m) => (
                  <button key={m.id}
                    data-testid={`greeter-mood-${m.id}`}
                    onClick={() => pickMood(m.id)}
                    style={{
                      padding: "8px 12px", borderRadius: 22,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "#dfe5f3", cursor: "pointer", fontSize: 13,
                      display: "inline-flex", alignItems: "center", gap: 6,
                      transition: "all .18s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#F26B1F";
                      e.currentTarget.style.background = "rgba(242,107,31,0.10)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}>
                    <span>{m.emoji}</span> <span>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="greeter-comfort" style={{
              padding: 14, marginBottom: 16, borderRadius: 10,
              background: "rgba(242,107,31,0.10)",
              border: "1px solid rgba(242,107,31,0.30)",
              display: "flex", alignItems: "center", gap: 10,
              color: "#ffe5d3", fontSize: 14, lineHeight: 1.5,
            }}>
              <Heart size={16} style={{ color: "#F26B1F", flexShrink: 0 }} />
              {COMFORT[picked]}
            </div>
          )}

          {/* Fact + Tip */}
          <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
            <div style={{
              display: "flex", gap: 10, padding: "10px 12px", borderRadius: 8,
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)", fontSize: 12,
              color: "#cdd5e7", lineHeight: 1.5,
            }}>
              <Sparkles size={14} style={{ color: "#7AB8FF", flexShrink: 0, marginTop: 2 }} />
              <span><b style={{ color: "#dfe5f3" }}>Did you know · </b>{data.fact}</span>
            </div>
            <div style={{
              display: "flex", gap: 10, padding: "10px 12px", borderRadius: 8,
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)", fontSize: 12,
              color: "#cdd5e7", lineHeight: 1.5,
            }}>
              <Lightbulb size={14} style={{ color: "#FBBC04", flexShrink: 0, marginTop: 2 }} />
              <span><b style={{ color: "#dfe5f3" }}>Tip · </b>{data.tip}</span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button data-testid="greeter-continue" onClick={() => close(false)}
              style={{
                padding: "10px 18px", borderRadius: 8,
                background: "#F26B1F", color: "#fff",
                border: "none", cursor: "pointer", fontSize: 13,
                fontWeight: 600, letterSpacing: "0.02em",
              }}>
              Let's get to work
            </button>
            <button data-testid="greeter-skip-today" onClick={() => close(true)}
              style={{
                padding: "10px 16px", borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.16)",
                color: "#9aa4bf", cursor: "pointer", fontSize: 12,
              }}>
              Skip for today
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes greeterFade { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}
